import type { LeaveRequest } from "@spork/s4p-server-sdk";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";

import { toastError, toastSuccess } from "@/components/AppToast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/text";
import {
    canParentResubmitLeaveRequest,
    canParentUpdateLeaveRequestEndDate,
} from "@/features/home/leaveRequestStatus";
import { getLatestInfoRequestNote } from "@/features/requests/leaveRequestEvents";
import {
    useDeferredAttachmentStaffNotify,
    type DeferredAttachmentStaffNotify,
} from "@/features/requests/useDeferredAttachmentStaffNotify";
import { log } from "@/logger";
import {
    useResubmitLeaveRequestMutation,
    useUpdateLeaveRequestEndDateMutation,
} from "@/query/apiSlice";
import { TEST_IDS } from "@/testIdDefinition";

import { ResubmitRequestCard } from "./ResubmitRequestCard";
import { UpdateEndDateSheet } from "./UpdateEndDateSheet";

type LeaveRequestCollapsedActionsProps = {
    request: LeaveRequest;
    /**
     * Shared deferred buffer from the detail screen so resubmit and the
     * attachments panel do not send duplicate staff emails.
     */
    deferredAttachments?: Pick<
        DeferredAttachmentStaffNotify,
        "rememberAdded" | "forgetRemoved" | "clear" | "flush"
    >;
};

function getErrorMessage(error: unknown, fallback: string): string {
    if (error && typeof error === "object" && "message" in error) {
        const message = (error as { message?: unknown }).message;
        if (typeof message === "string" && message.trim()) {
            return message;
        }
    }

    return fallback;
}

/** Actions that were visible on the list without expanding the accordion. */
export function LeaveRequestCollapsedActions({
    request,
    deferredAttachments,
}: LeaveRequestCollapsedActionsProps) {
    const { t } = useTranslation();
    const [isResubmitOpen, setIsResubmitOpen] = useState(false);
    const [isUpdateEndDateOpen, setIsUpdateEndDateOpen] = useState(false);
    const [attachmentsBusy, setAttachmentsBusy] = useState(false);
    const localDeferred = useDeferredAttachmentStaffNotify({ autoFlush: false });
    const deferred = deferredAttachments ?? localDeferred;
    const [resubmitLeaveRequest, { isLoading: isResubmitting }] =
        useResubmitLeaveRequestMutation();
    const [updateLeaveRequestEndDate, { isLoading: isUpdatingEndDate }] =
        useUpdateLeaveRequestEndDateMutation();

    const canResubmit = canParentResubmitLeaveRequest(request.status);
    const canUpdateEndDate = canParentUpdateLeaveRequestEndDate(
        request.status,
        request.reason,
    );
    const infoNote = canResubmit ? getLatestInfoRequestNote(request) : null;
    const isBusy = isResubmitting || isUpdatingEndDate || attachmentsBusy;
    const isOpenEnded = request.date_to == null || request.date_to === "";

    if (!canResubmit && !canUpdateEndDate) {
        return null;
    }

    async function handleResubmit(message: string) {
        try {
            const trimmed = message.trim();
            await resubmitLeaveRequest({
                id: request.id,
                ...(trimmed ? { message: trimmed } : {}),
            }).unwrap();
            // Resubmit email already lists new files — do not notifyAdded later.
            deferred.clear();
            setIsResubmitOpen(false);
            toastSuccess(
                t("leave.resubmittedTitle"),
                t("leave.resubmittedBody"),
            );
        } catch (error) {
            log.error("resubmitLeaveRequest failed", error);
            toastError(
                t("leave.errorTitle"),
                getErrorMessage(error, t("leave.resubmitFailed")),
            );
        }
    }

    async function handleUpdateEndDate(dateTo: string, timeTo: string | null) {
        try {
            const updated = await updateLeaveRequestEndDate({
                id: request.id,
                date_to: dateTo,
                time_to: timeTo,
            }).unwrap();
            setIsUpdateEndDateOpen(false);

            toastSuccess(
                updated.status === "pending"
                    ? t("requests.awaitingSchoolTitle")
                    : t("leave.endDateUpdatedTitle"),
                t("leave.endDateUpdatedBody"),
            );
        } catch (error) {
            log.error("updateLeaveRequestEndDate failed", error);
            toastError(
                t("leave.errorTitle"),
                getErrorMessage(error, t("leave.updateEndDateFailed")),
            );
        }
    }

    return (
        <>
            {canResubmit ? (
                <View className="mb-3 gap-3">
                    <Alert icon="circle-info">
                        <AlertTitle>{t("leave.schoolRequestTitle")}</AlertTitle>
                        <AlertDescription
                            testID={
                                TEST_IDS[
                                    TEST_IDS.REQUESTS_LIST_ITEM_SCHOOL_MESSAGE
                                ]
                            }
                        >
                            {infoNote ?? t("leave.infoRequestedDefault")}
                        </AlertDescription>
                    </Alert>
                    <Button
                        size="sm"
                        variant="outline"
                        className="self-start"
                        testID={
                            TEST_IDS[
                                TEST_IDS.REQUESTS_LIST_ITEM_RESUBMIT_OPEN_BUTTON
                            ]
                        }
                        disabled={isBusy}
                        onPress={() => setIsResubmitOpen(true)}
                    >
                        <Text className="text-primary">
                            {t("leave.addInformation")}
                        </Text>
                    </Button>
                </View>
            ) : null}
            {canUpdateEndDate ? (
                <Button
                    size="sm"
                    variant="outline"
                    className="mb-3 self-start"
                    testID={
                        TEST_IDS[
                            TEST_IDS.REQUESTS_LIST_ITEM_UPDATE_END_DATE_OPEN_BUTTON
                        ]
                    }
                    disabled={isBusy}
                    onPress={() => setIsUpdateEndDateOpen(true)}
                >
                    <Text className="text-primary">
                        {isOpenEnded
                            ? t("leave.setEndDate")
                            : t("leave.changeEndDate")}
                    </Text>
                </Button>
            ) : null}
            {canResubmit ? (
                <ResubmitRequestCard
                    visible={isResubmitOpen}
                    onClose={() => {
                        setIsResubmitOpen(false);
                        deferred.flush();
                    }}
                    request={request}
                    formKey={`${request.id}-${request.status}-${infoNote ?? ""}`}
                    infoNote={infoNote}
                    isBusy={isBusy}
                    isResubmitting={isResubmitting}
                    onResubmit={handleResubmit}
                    onAttachmentAdded={deferred.rememberAdded}
                    onAttachmentRemoved={deferred.forgetRemoved}
                    onAttachmentsBusyChange={setAttachmentsBusy}
                />
            ) : null}
            {canUpdateEndDate ? (
                <UpdateEndDateSheet
                    visible={isUpdateEndDateOpen}
                    onClose={() => setIsUpdateEndDateOpen(false)}
                    request={request}
                    formKey={`${request.id}-${request.date_to ?? ""}-${request.time_to ?? ""}`}
                    isBusy={isBusy}
                    isSaving={isUpdatingEndDate}
                    onSave={handleUpdateEndDate}
                />
            ) : null}
        </>
    );
}
